import { useState } from 'react';
import { Info, KeyRound, Loader2, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Section } from './panels';

/**
 * Settings → Security. Self-serve password change for the signed-in user.
 *
 * Lifted out of the former Account-page "Team & login" password modal into a
 * proper inline form. Uses supabase.auth.updateUser({ password }); the current
 * password isn't required because the user is already authenticated — same
 * behaviour the old modal had.
 */
export default function SecurityTab() {
  const { session } = useSession();
  const email = session?.user?.email ?? '';

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    if (next.length < 8) {
      setFeedback({ kind: 'err', msg: 'Password must be at least 8 characters.' });
      return;
    }
    if (next !== confirm) {
      setFeedback({ kind: 'err', msg: 'Passwords do not match.' });
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: next });
    setSaving(false);
    if (error) {
      setFeedback({ kind: 'err', msg: error.message });
      return;
    }
    setFeedback({ kind: 'ok', msg: 'Password updated.' });
    setNext('');
    setConfirm('');
  }

  return (
    <Section
      icon={<Lock size={18} />}
      title="Security"
      subtitle="Change the password you use to sign in."
    >
      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        {email && (
          <p className="text-sm tracking-tight text-slate-900">
            Signed in as <span className="font-semibold">{email}</span>
          </p>
        )}

        <div>
          <Label className="text-xs font-semibold tracking-tight text-slate-900">
            New password
          </Label>
          <div className="mt-1">
            <PasswordInput
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-tight text-slate-900">
            Confirm new password
          </Label>
          <div className="mt-1">
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>

        <div className="flex items-start gap-1.5 text-xs italic tracking-tight text-slate-900/70">
          <Info size={11} className="mt-0.5 shrink-0" />
          At least 8 characters. Your current password isn't required — you're already signed in.
        </div>

        {feedback && (
          <div
            className={cn(
              'text-xs tracking-tight',
              feedback.kind === 'ok' ? 'text-emerald-700' : 'text-red-600',
            )}
          >
            {feedback.msg}
          </div>
        )}

        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
          {saving ? 'Saving…' : 'Update password'}
        </Button>
      </form>
    </Section>
  );
}
